import React, { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  GeneralList,
  GeneralListSortableTh,
  LockIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  useGeneralListPageSize,
  useGeneralListSort,
} from '../../../components/ui';
import { ListRefreshHint } from '../../../components/generic/ListRefreshHint';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { CoaBulkUpdateBar } from './CoaBulkUpdateBar';
import {
  accountCanDelete,
  filterAccountList,
  getDescendantLedgerAccounts,
  getDirectLedgerAccounts,
} from './treeListUtils';
import type { AccAccountTreeNode } from './useData';

type ScopeTab = 'direct' | 'all';

export type CoaCreateAction = {
  id: 'account' | 'subgroup';
  label: string;
  hint: string;
  primary?: boolean;
  onClick: () => void;
};

type Props = {
  title: string;
  description?: string;
  selectedGroup: AccAccountTreeNode | null;
  canEdit: boolean;
  canDelete: boolean;
  isLoading?: boolean;
  createActions: CoaCreateAction[];
  onEditSelected: (() => void) | null;
  onViewAccount: (row: AccAccountTreeNode) => void;
  onEditAccount: (row: AccAccountTreeNode) => void;
  onDeleteAccount: (row: AccAccountTreeNode) => void;
  onSelectSubgroup?: (id: number) => void;
  isRefreshing?: boolean;
  lastRefreshedAt?: Date | null;
  onRefresh?: () => void;
  includeSubgroups: boolean;
  onIncludeSubgroupsChange: (value: boolean) => void;
};

function sortValue(row: AccAccountTreeNode, key: string): string {
  switch (key) {
    case 'code':
      return (row.combLevel ?? row.code ?? '').toLowerCase();
    case 'name':
      return (row.name ?? '').toLowerCase();
    case 'opType':
      return (
        row.operationalType?.code ??
        row.effectiveOperationalType?.code ??
        row.effectiveAccountType?.code ??
        ''
      ).toLowerCase();
    case 'dept':
      return (row.department?.code ?? '').toLowerCase();
    case 'status':
      return row.isActive ? 'active' : 'inactive';
    default:
      return '';
  }
}

function typeLabel(row: AccAccountTreeNode): string | null {
  if (row.operationalType) return row.operationalType.code;
  if (row.effectiveOperationalType ?? row.effectiveAccountType) {
    return `${(row.effectiveOperationalType ?? row.effectiveAccountType)!.code}*`;
  }
  return null;
}

export const CoaChildrenInbox: React.FC<Props> = ({
  title,
  description,
  selectedGroup,
  canEdit,
  canDelete,
  isLoading,
  createActions,
  onEditSelected,
  onViewAccount,
  onEditAccount,
  onDeleteAccount,
  onSelectSubgroup,
  isRefreshing,
  lastRefreshedAt,
  onRefresh,
  includeSubgroups,
  onIncludeSubgroupsChange,
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [tab, setTab] = useState<ScopeTab>(includeSubgroups ? 'all' : 'direct');
  const [page, setPage] = useState(1);
  const { itemsPerPage, setItemsPerPage, pageSizeOptions } = useGeneralListPageSize();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  }, [title, selectedGroup?.id]);

  useEffect(() => {
    setTab(includeSubgroups ? 'all' : 'direct');
  }, [includeSubgroups]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearch(searchInput), 300);
    return () => window.clearTimeout(t);
  }, [searchInput]);

  const { sortConfig, handleSort } = useGeneralListSort({ key: 'code', direction: 'asc' });

  useEffect(() => {
    setPage(1);
  }, [search, tab, itemsPerPage, sortConfig.key, sortConfig.direction]);

  const subgroups = useMemo(
    () => (selectedGroup?.children ?? []).filter((c) => !c.isPosting),
    [selectedGroup],
  );

  const directAccounts = useMemo(
    () => getDirectLedgerAccounts(selectedGroup),
    [selectedGroup],
  );
  const allAccounts = useMemo(
    () => getDescendantLedgerAccounts(selectedGroup),
    [selectedGroup],
  );

  const scopeAccounts = tab === 'all' ? allAccounts : directAccounts;
  const childRows = useMemo(() => filterAccountList(scopeAccounts, search), [scopeAccounts, search]);

  const counts = useMemo(
    () => ({
      direct: directAccounts.length,
      all: allAccounts.length,
      subgroups: subgroups.length,
    }),
    [directAccounts.length, allAccounts.length, subgroups.length],
  );

  const viewTabs = useMemo(
    () => [
      {
        id: 'direct' as const,
        label: 'Direct',
        icon: <DocumentTextIcon className="h-4 w-4" />,
        count: counts.direct,
        emptyTitle: 'No direct accounts',
        emptyHint:
          counts.subgroups > 0
            ? 'No ledger accounts directly under this group. Switch to All or pick a subgroup.'
            : 'Add the first ledger account under this group.',
      },
      {
        id: 'all' as const,
        label: 'All',
        icon: <FolderIcon className="h-4 w-4" />,
        count: counts.all,
        emptyTitle: 'No accounts',
        emptyHint: 'No ledger accounts in this group or its subgroups.',
      },
    ],
    [counts],
  );

  const sortedRows = useMemo(() => {
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    return [...childRows].sort(
      (a, b) =>
        sortValue(a, sortConfig.key).localeCompare(sortValue(b, sortConfig.key), undefined, {
          numeric: true,
          sensitivity: 'base',
        }) * dir,
    );
  }, [childRows, sortConfig.key, sortConfig.direction]);

  const total = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage) || 1);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const pageRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  const showingSubgroups = subgroups.length > 0 && tab === 'direct' && !search.trim() && page === 1;
  const hasFilters = Boolean(search.trim());
  const activeTabMeta = viewTabs.find((t) => t.id === tab) ?? viewTabs[0];
  const primaryCreate = createActions.find((a) => a.primary) ?? createActions[0];

  const clearFilters = () => {
    setSearchInput('');
    setSearch('');
  };

  const handleTabChange = (value: string) => {
    const next = value as ScopeTab;
    setTab(next);
    onIncludeSubgroupsChange(next === 'all');
  };

  return (
    <GeneralList
      className="h-full min-h-0"
      title={title}
      description={description}
      tabs={viewTabs.map(({ id, label, icon, count }) => ({ id, label, icon, count }))}
      activeTab={tab}
      onTabChange={handleTabChange}
      sorting={{ sortConfig, onSort: handleSort }}
      search={{
        value: searchInput,
        onChange: setSearchInput,
        placeholder: 'Search accounts…',
        title: 'Searches code, name, department',
      }}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {onRefresh ? (
            <ListRefreshHint
              colors={colors}
              isRefreshing={Boolean(isRefreshing)}
              lastRefreshedAt={lastRefreshedAt ?? null}
              onRefresh={onRefresh}
            />
          ) : null}
          {onEditSelected ? (
            <button
              type="button"
              onClick={onEditSelected}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl border px-3 py-2 text-sm font-medium',
                colors.border,
                colors.text,
              )}
            >
              <PencilIcon className="h-4 w-4" />
              Edit
            </button>
          ) : null}
          {primaryCreate ? (
            <button
              type="button"
              onClick={primaryCreate.onClick}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-xl px-3.5 py-2 text-sm font-medium text-white shadow-sm',
                colors.primary,
              )}
            >
              <PlusIcon className="h-4 w-4" />
              Add {primaryCreate.label}
            </button>
          ) : null}
        </div>
      }
      tabTrailing={
        createActions.length > 1 ? (
          <div className="flex flex-wrap items-center gap-1">
            {createActions
              .filter((a) => a !== primaryCreate)
              .map((action) => (
                <button
                  key={action.id}
                  type="button"
                  onClick={action.onClick}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition',
                    colors.textMuted,
                    colors.interactiveHover,
                  )}
                  title={action.hint}
                >
                  <PlusIcon className="h-3.5 w-3.5 opacity-70" />
                  {action.label}
                </button>
              ))}
          </div>
        ) : undefined
      }
      banner={
        canEdit && sortedRows.length > 0 ? (
          <CoaBulkUpdateBar
            accountIds={sortedRows.map((a) => a.id)}
            disabled={Boolean(isLoading)}
            onUpdated={() => {
              onRefresh?.();
            }}
          />
        ) : undefined
      }
      resultSummary={
        !isLoading ? (
          <p className={cn('text-xs', colors.textMuted)}>
            {total === 1 ? '1 account' : `${total} accounts`}
            {hasFilters ? ' matching filters' : ''}
            {subgroups.length > 0 ? ` · ${subgroups.length} subgroup${subgroups.length === 1 ? '' : 's'}` : ''}
          </p>
        ) : (
          <span />
        )
      }
      hasFilters={hasFilters}
      onClearFilters={clearFilters}
      isLoading={Boolean(isLoading)}
      loadingLabel="Loading…"
      isEmpty={pageRows.length === 0 && !showingSubgroups}
      emptyState={{
        icon: <DocumentTextIcon className="h-12 w-12" />,
        title: !selectedGroup
          ? 'Select a group'
          : hasFilters
            ? 'No matching accounts'
            : activeTabMeta.emptyTitle,
        hint: !selectedGroup
          ? 'Choose a group on the left to view ledger accounts.'
          : hasFilters
            ? 'Try adjusting the search.'
            : activeTabMeta.emptyHint,
        action: !selectedGroup ? undefined : hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl border px-4 py-2.5 text-sm font-medium',
              colors.border,
              colors.text,
            )}
          >
            Clear search
          </button>
        ) : primaryCreate ? (
          <button
            type="button"
            onClick={primaryCreate.onClick}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-medium text-white',
              colors.primary,
            )}
          >
            <PlusIcon className="h-4 w-4" />
            Add {primaryCreate.label}
          </button>
        ) : undefined,
      }}
      pagination={{
        page,
        totalPages,
        total,
        itemsPerPage,
        pageSizeOptions,
        onPageChange: setPage,
        onItemsPerPageChange: (size) => {
          setItemsPerPage(size);
          setPage(1);
        },
      }}
    >
      <table className="w-max border-separate border-spacing-0 text-sm">
        <thead>
          <tr className={cn('border-b text-left text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
            <th
              className={cn('sticky left-0 z-20 whitespace-nowrap px-3 py-3 font-medium', colors.surface)}
              style={{ minWidth: '5.5rem' }}
            />
            <GeneralListSortableTh sortKey="code" className="whitespace-nowrap" style={{ minWidth: '9rem' }}>
              Code
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="name" className="whitespace-nowrap" style={{ minWidth: '14rem' }}>
              Name
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="opType" className="whitespace-nowrap" style={{ minWidth: '7rem' }}>
              Op. type
            </GeneralListSortableTh>
            <GeneralListSortableTh sortKey="dept" className="whitespace-nowrap" style={{ minWidth: '6rem' }}>
              Dept
            </GeneralListSortableTh>
            <th className="whitespace-nowrap px-3 py-3 font-medium" style={{ minWidth: '7rem' }}>
              Flags
            </th>
            <GeneralListSortableTh sortKey="status" className="whitespace-nowrap" style={{ minWidth: '6rem' }}>
              Status
            </GeneralListSortableTh>
          </tr>
        </thead>
        <tbody>
          {showingSubgroups
            ? subgroups.map((group) => (
                <tr
                  key={`g:${group.id}`}
                  onClick={() => onSelectSubgroup?.(group.id)}
                  className={cn(
                    'group cursor-pointer border-b transition last:border-b-0',
                    colors.border,
                    colors.surface,
                    colors.interactiveHover,
                  )}
                >
                  <td
                    className="sticky left-0 z-10 whitespace-nowrap bg-inherit px-3 py-3.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="flex flex-nowrap items-center">
                      <button
                        type="button"
                        title="Open subgroup"
                        aria-label="Open subgroup"
                        onClick={() => onSelectSubgroup?.(group.id)}
                        className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3.5">
                    <span className="inline-flex items-center gap-1.5">
                      <FolderIcon className={cn('h-3.5 w-3.5', isDark ? 'text-violet-400' : 'text-violet-500')} />
                      <span className={cn('tabular-nums', colors.text)}>{group.combLevel ?? group.code}</span>
                    </span>
                  </td>
                  <td className="max-w-[18rem] px-3 py-3.5">
                    <p className={cn('truncate font-medium', colors.text)} title={group.name}>
                      {group.name}
                    </p>
                    <p className={cn('text-[11px]', colors.textMuted)}>Subgroup</p>
                  </td>
                  <td className={cn('px-3 py-3.5', colors.textMuted)}></td>
                  <td className={cn('px-3 py-3.5', colors.textMuted)}></td>
                  <td className={cn('px-3 py-3.5', colors.textMuted)}></td>
                  <td className="px-3 py-3.5">
                    <Badge variant={group.isActive ? 'success' : 'secondary'} className="rounded-md px-2 py-0.5 font-normal">
                      {group.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                </tr>
              ))
            : null}
          {pageRows.map((row) => {
            const deletable = accountCanDelete(row);
            const label = typeLabel(row);
            return (
              <tr
                key={row.id}
                onClick={() => onViewAccount(row)}
                className={cn(
                  'group cursor-pointer border-b transition last:border-b-0',
                  !row.isActive && 'opacity-60',
                  colors.border,
                  colors.surface,
                  colors.interactiveHover,
                )}
              >
                <td
                  className="sticky left-0 z-10 whitespace-nowrap bg-inherit px-3 py-3.5"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-nowrap items-center">
                    <button
                      type="button"
                      title="View"
                      aria-label="View"
                      onClick={() => onViewAccount(row)}
                      className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                    >
                      <EyeIcon className="h-5 w-5" />
                    </button>
                    {canEdit ? (
                      <button
                        type="button"
                        title="Edit"
                        aria-label="Edit"
                        onClick={() => onEditAccount(row)}
                        className={cn('rounded-lg p-1 transition-colors', colors.primaryFg, colors.interactiveHover)}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                    {canDelete ? (
                      <button
                        type="button"
                        title={deletable ? 'Delete' : 'Cannot delete — has children or journal lines'}
                        aria-label="Delete"
                        disabled={!deletable}
                        onClick={() => deletable && onDeleteAccount(row)}
                        className={cn(
                          'rounded-lg p-1 transition-colors',
                          deletable
                            ? cn(isDark ? 'text-red-400' : 'text-red-600', colors.interactiveHover)
                            : cn(colors.textMuted, 'cursor-not-allowed'),
                        )}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    ) : null}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  <span className="inline-flex items-center gap-1.5">
                    <DocumentTextIcon
                      className={cn('h-3.5 w-3.5 shrink-0', isDark ? 'text-sky-400' : 'text-sky-600')}
                    />
                    <span className={cn('tabular-nums', colors.text)} title={row.combLevel ?? row.code}>
                      {row.combLevel ?? row.code}
                    </span>
                    {row.usedInRecords ? (
                      <span
                        title="Used in journals"
                        className={cn('shrink-0', isDark ? 'text-amber-400/80' : 'text-amber-600/80')}
                      >
                        <LockIcon className="h-3.5 w-3.5" />
                      </span>
                    ) : null}
                  </span>
                </td>
                <td className="max-w-[18rem] px-3 py-3.5">
                  <p className={cn('truncate font-medium', colors.text)} title={row.name}>
                    {row.name}
                  </p>
                </td>
                <td
                  className={cn('whitespace-nowrap px-3 py-3.5 tabular-nums', colors.textMuted)}
                  title={
                    row.operationalType?.name ??
                    row.effectiveOperationalType?.name ??
                    row.effectiveAccountType?.name
                  }
                >
                  {label ?? ''}
                </td>
                <td className={cn('whitespace-nowrap px-3 py-3.5', colors.textMuted)}>
                  {row.department?.code ?? ''}
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  <div className="flex flex-wrap gap-1">
                    {row.allowOpenItems ? (
                      <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide', colors.warning)}>
                        OI
                      </span>
                    ) : null}
                    {row.isIntercompany ? (
                      <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide', colors.info)}>
                        IC
                      </span>
                    ) : null}
                    {row.isBudget ? (
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                          isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-800',
                        )}
                      >
                        Bud
                      </span>
                    ) : null}
                    {!row.allowOpenItems && !row.isIntercompany && !row.isBudget ? (
                      <span className={colors.textMuted}></span>
                    ) : null}
                  </div>
                </td>
                <td className="whitespace-nowrap px-3 py-3.5">
                  <Badge variant={row.isActive ? 'success' : 'secondary'} className="rounded-md px-2 py-0.5 font-normal">
                    {row.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </GeneralList>
  );
};
